import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { Density } from "@tc/shared";
import { updatePreferences } from "@/features/account/api";

/**
 * Interface density (TW-3466). Holds the live `regular`/`compact` choice that drives the `.dense`
 * modifier on the shell root (which collapses `--gap`/`--pad` to the compact scale, app-wide). It
 * is seeded once from the server preference (`me.density`) — RoleLayout only mounts this after
 * /api/me resolves, so the initial value is always the real one (mirrors LanguageProvider). The
 * choice is persisted best-effort via PATCH /api/me/preferences; on reload the server value
 * re-seeds it, so it survives reloads and is reflected on /api/me.
 */
interface DensityValue {
  density: Density;
  setDensity: (next: Density) => void;
}

const DensityContext = createContext<DensityValue | null>(null);

export function DensityProvider({ initial, children }: { initial: Density; children: ReactNode }) {
  const [density, setDensityState] = useState<Density>(initial);

  const setDensity = useCallback(
    (next: Density) => {
      setDensityState((prev) => {
        if (next === prev) return prev;
        // Persist best-effort; the live class already reflects the choice for this session.
        void updatePreferences({ density: next }).catch(() => undefined);
        return next;
      });
    },
    [],
  );

  const value = useMemo<DensityValue>(() => ({ density, setDensity }), [density, setDensity]);
  return <DensityContext.Provider value={value}>{children}</DensityContext.Provider>;
}

/** Read/set the interface density. Throws if used outside <DensityProvider>. */
export function useDensity(): DensityValue {
  const ctx = useContext(DensityContext);
  if (!ctx) throw new Error("useDensity must be used within <DensityProvider>");
  return ctx;
}
